import { useEffect, useState } from "react";
import { Loader2, UserPlus } from "lucide-react";
import { DashShell, StatCard } from "@/components/dash/DashShell";
import { ADMIN_NAV } from "./nav";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminBdes() {
    const [bdes, setBdes] = useState([]);
    const [perf, setPerf] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({});

    const load = () => {
        setLoading(true);
        Promise.all([api.get("/admin/bdes"), api.get("/admin/bde-performance")]).then(([a, b]) => { setBdes(a.data); setPerf(b.data); }).finally(() => setLoading(false));
    };
    useEffect(load, []);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/admin/bdes", { name: form.name, email: form.email, password: form.password, phone: form.phone || "" });
            toast.success("BDE created"); setOpen(false); setForm({}); load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    return (
        <DashShell nav={ADMIN_NAV} title="BDE Management">
            <div className="flex justify-between items-center mb-5">
                <h2 className="text-teal-900/70 text-sm">Team & performance overview</h2>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><button className="btn-gold rounded-full px-5 py-2.5 text-sm flex items-center gap-2" data-testid="add-bde-btn"><UserPlus className="w-4 h-4" /> Add BDE</button></DialogTrigger>
                    <DialogContent className="max-w-md">
                        <DialogHeader><DialogTitle className="font-serif text-xl">Create BDE Account</DialogTitle></DialogHeader>
                        <form onSubmit={create} className="space-y-3">
                            <div className="space-y-1.5"><Label className="text-sm">Name</Label><Input value={form.name || ""} onChange={set("name")} data-testid="bde-name" required /></div>
                            <div className="space-y-1.5"><Label className="text-sm">Email</Label><Input type="email" value={form.email || ""} onChange={set("email")} data-testid="bde-email" required /></div>
                            <div className="space-y-1.5"><Label className="text-sm">Phone</Label><Input value={form.phone || ""} onChange={set("phone")} data-testid="bde-phone" /></div>
                            <div className="space-y-1.5"><Label className="text-sm">Password</Label><Input type="password" value={form.password || ""} onChange={set("password")} data-testid="bde-password" required /></div>
                            <button type="submit" className="btn-gold w-full rounded-full py-2.5 text-sm" data-testid="bde-create-submit">Create</button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {loading ? <div className="grid place-items-center py-24 text-teal-900/40"><Loader2 className="w-7 h-7 animate-spin" /></div> : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {perf.map((p) => (
                        <div key={p.id} className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm" data-testid={`bde-card-${p.id}`}>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-11 h-11 rounded-full bg-teal-900 text-cream grid place-items-center font-serif">{p.name.charAt(0)}</div>
                                <div><div className="font-semibold text-teal-900">{p.name}</div><div className="text-xs text-teal-900/50">{bdes.find((b) => b.id === p.id)?.email}</div></div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-center">
                                <Metric label="Assigned" value={p.assigned} />
                                <Metric label="Contacted" value={p.contacted} />
                                <Metric label="Converted" value={p.converted} accent />
                                <Metric label="Follow-ups" value={p.followups} />
                            </div>
                        </div>
                    ))}
                    {!perf.length && <p className="text-teal-900/40 text-sm">No BDEs yet.</p>}
                </div>
            )}
        </DashShell>
    );
}
function Metric({ label, value, accent }) {
    return <div className="bg-teal-900/[0.03] rounded-xl py-3"><div className={`font-mono font-bold text-xl ${accent ? "text-growth" : "text-teal-900"}`}>{value}</div><div className="text-xs text-teal-900/50">{label}</div></div>;
}
